import moment from "moment";
import React, { useMemo } from "react";
import {
  DATE_FORMAT_STRING_WITH_YEAR_FIRST,
  SHORT_DATE_WITH_MONTH_DAY,
  SHORT_DATE_WITH_HOUR_MINUTE,
  DATE_FORMAT_STRING_FOR_HOUR_MINUTE_AM_PM,
} from "src/constants/dataTimeFormats";

const COL_WIDTH = 56;
const CHART_HEIGHT = 200;
const TIME_BANDS = [
  { label: "12 AM", value: 0 },
  { label: "04 AM", value: 4 },
  { label: "08 AM", value: 8 },
  { label: "12 PM", value: 12 },
  { label: "04 PM", value: 16 },
  { label: "08 PM", value: 20 },
];

const COLORS = {
  Taken: "#1A73E8",
  Missed: "#EA4335",
  Scheduled: "#1A73E8",
} as const;

export type MedicationScheduleViewMode = "week" | "month";

export interface MedicationScheduleItem {
  date?: string;
  time?: string;
  type?: "Taken" | "Missed" | "Scheduled";
  dose?: number;
  dose_count?: number;
  count?: number;
  [key: string]: any;
}

interface MedicationScheduleChartProps {
  /** Schedule items from API: { date, time, type, dose? } */
  data?: MedicationScheduleItem[] | null;
  viewMode?: MedicationScheduleViewMode;
  /** Start of period (week start or month start). Default: this week / this month */
  periodStart?: moment.Moment;
}

const timeToNumeric = (timeStr: string): number => {
  if (!timeStr) return 0;
  const parts = String(timeStr).split(":");
  const hours = parseFloat(parts[0]) || 0;
  const minutes = parts[1] ? parseFloat(parts[1]) : 0;
  return hours + minutes / 60;
};

const formatTimeLabel = (timeStr: string): string => {
  if (!timeStr) return "";
  const m = moment(
    timeStr,
    [SHORT_DATE_WITH_HOUR_MINUTE, "HH:mm", "hh:mm A"],
    true
  );
  if (!m.isValid()) return String(timeStr).substring(0, 5);
  const hour = m.hour();
  const minute = m.minute();
  const suffix = hour >= 12 ? "PM" : "AM";
  const h12 = hour % 12 || 12;
  return minute > 0
    ? `${h12}:${String(minute).padStart(2, "0")}${suffix}`
    : `${h12}${suffix}`;
};

/** Get dose to display from item (API may send dose, dose_count, or count) */
const getDose = (item: MedicationScheduleItem): number | null => {
  const d = item?.dose ?? item?.dose_count ?? item?.count;
  if (d == null) return null;
  const n = Number(d);
  return Number.isFinite(n) ? n : null;
};

const MedicationScheduleChart: React.FC<MedicationScheduleChartProps> = ({
  data = [],
  viewMode = "week",
  periodStart,
}) => {
  const { dayLabels, dayCount, dateToIndex, processedPoints } = useMemo(() => {
    const start = periodStart
      ? moment(periodStart)
      : viewMode === "week"
      ? moment().startOf("week")
      : moment().startOf("month");

    let dayCount: number;
    let dayLabels: string[];

    if (viewMode === "week") {
      dayCount = 7;
      dayLabels = Array.from({ length: 7 }, (_, i) =>
        start.clone().add(i, "day").format("ddd")
      );
    } else {
      dayCount = start.daysInMonth();
      dayLabels = Array.from({ length: dayCount }, (_, i) =>
        start
          .clone()
          .date(i + 1)
          .format("D")
      );
    }

    const dateFormats = [
      DATE_FORMAT_STRING_WITH_YEAR_FIRST,
      SHORT_DATE_WITH_MONTH_DAY,
      "DD MMM",
    ];
    const dateToIndex = new Map<string, number>();
    for (let i = 0; i < dayCount; i++) {
      const d = start.clone().add(i, "day");
      const ymd = d.format(DATE_FORMAT_STRING_WITH_YEAR_FIRST);
      dateToIndex.set(ymd, i);
      const short = d.format(SHORT_DATE_WITH_MONTH_DAY);
      dateToIndex.set(short, i);
    }

    const points: Array<{
      dayIndex: number;
      timeNumeric: number;
      type: "Taken" | "Missed" | "Scheduled";
      timeLabel: string;
      dose: number | null;
      displayLabel: string;
    }> = [];

    if (!data || !Array.isArray(data)) {
      return { dayLabels, dayCount, dateToIndex, processedPoints: points };
    }

    data.forEach((item: MedicationScheduleItem) => {
      const m = moment(item.date, dateFormats, true);
      if (!m.isValid()) return;
      const idx =
        dateToIndex.get(m.format(DATE_FORMAT_STRING_WITH_YEAR_FIRST)) ??
        dateToIndex.get(m.format(SHORT_DATE_WITH_MONTH_DAY));
      if (idx == null || idx < 0 || idx >= dayCount) return;
      const type = (
        item.type === "Taken" ||
        item.type === "Missed" ||
        item.type === "Scheduled"
          ? item.type
          : "Scheduled"
      ) as "Taken" | "Missed" | "Scheduled";
      const timeNumeric = item.time ? timeToNumeric(item.time) : 12;
      const timeLabel = item.time ? formatTimeLabel(item.time) : "";
      const dose = getDose(item);
      const displayLabel =
        dose != null && dose > 0 ? `${timeLabel} (${dose})` : timeLabel;

      points.push({
        dayIndex: idx,
        timeNumeric,
        type,
        timeLabel,
        dose,
        displayLabel: displayLabel || timeLabel,
      });

      console.log(points);
    });

    return { dayLabels, dayCount, dateToIndex, processedPoints: points };
  }, [data, viewMode, periodStart]);

  const scrollWidth = dayCount * COL_WIDTH;
  const hasData = processedPoints.length > 0;

  if (!hasData && (!data || data.length === 0)) {
    return (
      <div className="w-full space-y-4">
        <div className="flex h-[228px] items-center justify-center rounded-lg border border-dashed border-gray-200 bg-gray-50">
          <p className="text-sm text-gray-500">No doses this period</p>
        </div>
        <div className="flex flex-row items-center justify-center gap-4 border-t border-gray-200 pt-4">
          <div className="flex items-center gap-1">
            <div className="h-2 w-2 rounded-full border-2 border-[#1A73E8] bg-white" />
            <span className="text-xs font-light">Scheduled</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="h-2 w-2 rounded-full bg-[#1A73E8]" />
            <span className="text-xs font-light">Taken</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="h-2 w-2 rounded-full bg-[#EA4335]" />
            <span className="text-xs font-light">Missed</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full space-y-4">
      <div className="flex w-full overflow-hidden rounded-lg border border-gray-200 bg-white">
        {/* Sticky Y-axis (time labels) */}
        <div
          className="flex flex-shrink-0 flex-col justify-between bg-gray-50 py-3 pl-2 pr-2"
          style={{ width: 56, height: CHART_HEIGHT + 28 }}
        >
          {TIME_BANDS.map((band) => (
            <span
              key={band.label}
              className="text-[11px] font-normal text-gray-500"
            >
              {band.label}
            </span>
          ))}
        </div>

        {/* Scrollable: grid + points + x-axis */}
        <div
          className="flex-1 overflow-x-auto overflow-y-hidden"
          style={{ minWidth: 0 }}
        >
          <div style={{ width: scrollWidth, minWidth: scrollWidth }}>
            <svg width={scrollWidth} height={CHART_HEIGHT} className="block">
              {/* Dotted grid: vertical lines per day */}
              {Array.from({ length: dayCount + 1 }, (_, i) => (
                <line
                  key={`v-${i}`}
                  x1={i * COL_WIDTH}
                  y1={0}
                  x2={i * COL_WIDTH}
                  y2={CHART_HEIGHT}
                  stroke="#E5E7EB"
                  strokeDasharray="3 3"
                />
              ))}
              {/* Dotted grid: horizontal lines (time bands) */}
              {TIME_BANDS.slice(0, -1).map((_, i) => {
                const y = ((i + 1) / TIME_BANDS.length) * CHART_HEIGHT;
                return (
                  <line
                    key={`h-${i}`}
                    x1={0}
                    y1={y}
                    x2={scrollWidth}
                    y2={y}
                    stroke="#E5E7EB"
                    strokeDasharray="3 3"
                  />
                );
              })}

              {/* Data points */}
              {processedPoints.map((pt, i) => {
                const x = pt.dayIndex * COL_WIDTH + COL_WIDTH / 2;
                const y = (1 - pt.timeNumeric / 24) * CHART_HEIGHT;
                const isScheduled = pt.type === "Scheduled";
                const color = COLORS[pt.type];
                return (
                  <g key={`${pt.dayIndex}-${pt.timeNumeric}-${i}`}>
                    <text
                      x={x}
                      y={Math.max(10, y - 12)}
                      textAnchor="middle"
                      style={{
                        fontSize: 10,
                        fill: color,
                        fontWeight: 500,
                      }}
                    >
                      {pt.displayLabel || ""}
                    </text>
                    <circle
                      cx={x}
                      cy={y}
                      r={4}
                      fill={isScheduled ? "white" : color}
                      stroke={color}
                      strokeWidth={1}
                    />
                  </g>
                );
              })}
            </svg>
            {/* X-axis labels (scroll with chart) */}
            <div
              className="flex border-t border-gray-200 bg-gray-50/50"
              style={{ width: scrollWidth, height: 28 }}
            >
              {dayLabels.map((label, i) => (
                <div
                  key={i}
                  className="flex flex-shrink-0 items-center justify-center text-[11px] font-medium text-gray-600 capitalize"
                  style={{ width: COL_WIDTH }}
                >
                  {label}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Legend */}
      <div className="flex flex-row items-center justify-center gap-4 border-t border-gray-200 pt-4">
        <div className="flex items-center gap-1">
          <div className="h-2 w-2 rounded-full border-2 border-[#1A73E8] bg-white" />
          <span className="text-xs font-light">Scheduled</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="h-2 w-2 rounded-full bg-[#1A73E8]" />
          <span className="text-xs font-light">Taken</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="h-2 w-2 rounded-full bg-[#EA4335]" />
          <span className="text-xs font-light">Missed</span>
        </div>
      </div>
    </div>
  );
};

export default MedicationScheduleChart;
